import React, {memo, useCallback, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useSelector} from 'react-redux';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {ConfigModel} from '../../../../store/control/state';
import {useHistory} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';
import {useLocalization} from '../../../../hooks/localization';

const BACK_URL = '/settings';

export const AlarmEditScreen = memo(function AlarmEditScreen() {
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const question = useQuestion();
    const {t} = useLocalization();

    const config = useSelector(state => state.control.config);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);

    const [homeTimer, setHomeTimer] = useState(config.alarm_settings.home_timer_seconds.toString());
    const [outsideTimer, setOutsideTimer] = useState(config.alarm_settings.outside_timer_seconds.toString());
    const [firstLevelAlarmCountdownDuration, setFirstLevelAlarmCountdownDuration] =
        useState(config.alarm.first_level_alarm_countdown_duration.toString());
    const [secondLevelAlarmCountdownDuration, setSecondLevelAlarmCountdownDuration] =
        useState(config.alarm.second_level_alarm_countdown_duration.toString());
    const [thirdLevelAlarmCountdownDuration, setThirdLevelAlarmCountdownDuration] =
        useState(config.alarm.third_level_alarm_countdown_duration.toString());
    const [reactivationDurationAfterReset, setReactivationDurationAfterReset] =
        useState(config.alarm.reactivation_duration_after_reset.toString());
    const [sirenIntervalOnDuration, setSirenIntervalOnDuration] =
        useState(config.alarm.siren_interval_on_duration.toString());
    const [sirenIntervalOffDuration, setSirenIntervalOffDuration] =
        useState(config.alarm.siren_interval_off_duration.toString());
    const [sirenRepeatCount, setSirenRepeatCount] =
        useState(config.alarm.siren_interval_repeat_count.toString());

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const save = useCallback(() => {
        const homeTimerValue = Number(homeTimer);
        if (isNaN(homeTimerValue) || homeTimerValue < 0) {
            failure(t('AT_HOME_TIMER_VALUE_IS_INVALID'));

            return;
        }

        const outsideTimerValue = Number(outsideTimer);
        if (isNaN(outsideTimerValue) || outsideTimerValue < 0) {
            failure(t('AT_OUTSIDE_TIMER_VALUE_IS_INVALID'));

            return;
        }

        const firstLevelAlarmCountdownDurationValue = Number(firstLevelAlarmCountdownDuration);
        if (isNaN(firstLevelAlarmCountdownDurationValue) || firstLevelAlarmCountdownDurationValue < 0) {
            failure(t('1ST_LEVEL_ALARM_COUNTDOWN_IS_INVALID'));

            return;
        }

        const secondLevelAlarmCountdownDurationValue = Number(secondLevelAlarmCountdownDuration);
        if (isNaN(secondLevelAlarmCountdownDurationValue) || secondLevelAlarmCountdownDurationValue < 0) {
            failure(t('2ND_LEVEL_ALARM_COUNTDOWN_IS_INVALID'));

            return;
        }

        const thirdLevelAlarmCountdownDurationValue = Number(thirdLevelAlarmCountdownDuration);
        if (isNaN(thirdLevelAlarmCountdownDurationValue) || thirdLevelAlarmCountdownDurationValue < 0) {
            failure(t('3RD_LEVEL_ALARM_COUNTDOWN_IS_INVALID'));

            return;
        }

        const reactivationDurationAfterResetValue = Number(reactivationDurationAfterReset);
        if (isNaN(reactivationDurationAfterResetValue) || reactivationDurationAfterResetValue < 0) {
            failure(t('REACTIVATION_DURATION_AFTER_ALARM_RESET_IS_INVALID'));

            return;
        }

        const sirenIntervalOnDurationValue = Number(sirenIntervalOnDuration);
        if (isNaN(sirenIntervalOnDurationValue) || sirenIntervalOnDurationValue < 0) {
            failure(t('SIREN_INTERVAL_ON_DURATION_IS_INVALID'));

            return;
        }

        const sirenIntervalOffDurationValue = Number(sirenIntervalOffDuration);
        if (isNaN(sirenIntervalOffDurationValue) || sirenIntervalOffDurationValue < 0) {
            failure(t('SIREN_INTERVAL_OFF_DURATION_IS_INVALID'));

            return;
        }

        const sirenRepeatCountValue = Number(sirenRepeatCount);
        if (isNaN(sirenRepeatCountValue) || sirenRepeatCountValue < 0) {
            failure(t('SIREN_REPEAT_COUNT_IS_INVALID'));

            return;
        }

        const configModel: ConfigModel = {
            ...config,
            alarm: {
                first_level_alarm_countdown_duration: firstLevelAlarmCountdownDurationValue,
                second_level_alarm_countdown_duration: secondLevelAlarmCountdownDurationValue,
                third_level_alarm_countdown_duration: thirdLevelAlarmCountdownDurationValue,
                reactivation_duration_after_reset: reactivationDurationAfterResetValue,
                siren_interval_on_duration: sirenIntervalOnDurationValue,
                siren_interval_off_duration: sirenIntervalOffDurationValue,
                siren_interval_repeat_count: sirenRepeatCountValue,
            },
            alarm_settings: {
                home_timer_seconds: homeTimerValue,
                outside_timer_seconds: outsideTimerValue,
            },
        };

        return rpc.updateConfigs(configModel).catch(failure);
    }, [
        t,
        config,
        homeTimer,
        outsideTimer,
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        failure,
        rpc,
    ]);

    const onSavePressed = useCallback(() => {
        save()
            .then(() => {
                success(t('SUCCESSFULLY_EDITED'));
                onBackPressed();
            });
    }, [
        t,
        save,
        success,
        onBackPressed,
    ]);

    const onApplyPressed = useCallback(() => {
        const count = bridgeDeviceList.length;
        question(t('SETTINGS_WILL_BE_APPLIED__CONTINUE', {count}), () => {
            save()
                .then(async () => {
                    for (const bridgeDevice of bridgeDeviceList) {
                        await rpc.updateBridgeDevice({
                            ...bridgeDevice, alarm_config: {
                                ...bridgeDevice.alarm_config,
                                first_level_alarm_countdown_duration: Number(firstLevelAlarmCountdownDuration),
                                second_level_alarm_countdown_duration: Number(secondLevelAlarmCountdownDuration),
                                third_level_alarm_countdown_duration: Number(thirdLevelAlarmCountdownDuration),
                                reactivation_duration_after_reset: Number(reactivationDurationAfterReset),
                                siren_interval_on_duration: Number(sirenIntervalOnDuration),
                                siren_interval_off_duration: Number(sirenIntervalOffDuration),
                                siren_interval_repeat_count: Number(sirenRepeatCount),
                            },
                        });
                    }
                })
                .then(() => {
                    success(t('SUCCESSFULLY_EDITED'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [
        t,
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        save,
        rpc,
        question,
        bridgeDeviceList,
        success,
        onBackPressed,
        failure,
    ]);

    const buttons = [
        {
            text: t('apply'),
            className: 'blue',
            onClick: onApplyPressed,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ];

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null);
    }, [setActiveNumpadInput]);

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentNumpadInput={currentNumpadInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <div className="line">
                            <span>{t('alarm_countdown_durations')}</span>
                        </div>
                        <SettingInputComponent
                            placeholder={t("1st_level")}
                            label={t("1st_level")}
                            value={firstLevelAlarmCountdownDuration}
                            setValue={setFirstLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t("2nd_level")}
                            label={t("2nd_level")}
                            value={secondLevelAlarmCountdownDuration}
                            setValue={setSecondLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t("3rd_level")}
                            label={t("3rd_level")}
                            value={thirdLevelAlarmCountdownDuration}
                            setValue={setThirdLevelAlarmCountdownDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <div className="line">
                            <span>{t('alar_reactivation_duration_after_reset')}</span>
                        </div>
                        <SettingInputComponent
                            placeholder={t("reactivation_duration")}
                            label={t("reactivation_duration_2")}
                            value={reactivationDurationAfterReset}
                            setValue={setReactivationDurationAfterReset}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                    </div>
                    <div className="content-wrapper">
                        <div className="line">
                            <span>{t('siren_interval')}</span>
                        </div>
                        <SettingInputComponent
                            placeholder={t("interval_on_duration")}
                            label={t("interval_on_duration_2")}
                            value={sirenIntervalOnDuration}
                            setValue={setSirenIntervalOnDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t("interval_off_duration")}
                            label={t("interval_off_duration_2")}
                            value={sirenIntervalOffDuration}
                            setValue={setSirenIntervalOffDuration}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t("interval_repeat_count")}
                            label={t("interval_repeat_count_2")}
                            value={sirenRepeatCount}
                            setValue={setSirenRepeatCount}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <div className="line">
                            <span>{t('alarm_setup_counter_durations')}</span>
                        </div>
                        <SettingInputComponent
                            placeholder={t("at_home")}
                            label={t("at_home")}
                            value={homeTimer}
                            setValue={setHomeTimer}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            placeholder={t("at_outside")}
                            label={t("at_outside")}
                            value={outsideTimer}
                            setValue={setOutsideTimer}
                            setActiveInput={setActiveNumpadInput}
                            tempValue="0"
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
